import type { UnitInfoDataInterface } from 'store/unitInfoData';
import type { UnitInterface } from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import { getDescriptionFromModelName } from 'unitData/Feeders/Pan/PanFeederSPData';
import { getModelName } from 'util/unitTypeGuards';

const getScreenTitle = (modelName: string) =>
  modelName ? `Screen ${modelName}` : 'Screen';

const getMobileTitle = (
  prefix: string,
  modelName: string,
  displayName?: string,
) => {
  const resolvedName = displayName?.length ? displayName : modelName;
  return resolvedName ? `${prefix} ${resolvedName}` : prefix;
};

export const getUnitPropertyPanelTitle = (
  unit: UnitInterface,
  unitInfoData: UnitInfoDataInterface,
) => {
  const modelName = getModelName(unit.unitInfo);

  switch (unit.type) {
    case UnitTypes.FEED:
      return 'Feed';
    case UnitTypes.STORAGE:
    case UnitTypes.STORAGE_FEEDER:
    case UnitTypes.STORAGE_FEEDER_2_OUTPUTS:
      return 'Storage';
    case UnitTypes.ADD:
      return 'Add';
    case UnitTypes.SPLIT:
      return 'Split';
    case UnitTypes.CONE_CRUSHER_CH:
    case UnitTypes.CONE_CRUSHER_CS:
      return modelName ? `Cone Crusher ${modelName}` : 'Cone Crusher';
    case UnitTypes.JAW_CRUSHER_CJ:
      return modelName ? `Jaw Crusher ${modelName}` : 'Jaw Crusher';
    case UnitTypes.IMPACT_CRUSHER_CI:
      return modelName ? `HSI Crusher ${modelName}` : 'HSI Crusher';
    case UnitTypes.IMPACT_CRUSHER_CV:
      return modelName ? `VSI Crusher ${modelName}` : 'VSI Crusher';
    case UnitTypes.GYRATORY_CRUSHER_CG:
      return modelName ? `Gyratory Crusher ${modelName}` : 'Gyratory Crusher';
    case UnitTypes.HYBRID_CRUSHER_CR:
      return modelName ? `Hybrid Crusher ${modelName}` : 'Hybrid Crusher';
    case UnitTypes.GENERIC_CRUSHER:
    case UnitTypes.GENERIC_SCREEN:
      return modelName ? `Generic ${modelName}` : 'Generic';
    case UnitTypes.CONVENTIONAL_SCREEN:
    case UnitTypes.LINEAR_MOTION_SCREEN:
    case UnitTypes.MULTISLOPE_SCREEN:
    case UnitTypes.KWATANI_SCREEN:
    case UnitTypes.FREEFALL_SCREEN_SF:
    case UnitTypes.FREEFALL_SCREEN_SS:
      return getScreenTitle(modelName);
    case UnitTypes.BANANA_SCREEN:
      return 'Multislope (Generic)';
    case UnitTypes.SCHENCK_SCREEN:
      return 'Sandvik / Schenck Screen';
    case UnitTypes.GRIZZLY_SCREEN:
      return modelName ? `Grizzly Screen ${modelName}` : 'Grizzly Screen';
    case UnitTypes.FEEDER_AND_SCREEN:
      return modelName ? `Feeder Screen ${modelName}` : 'Feeder Screen';
    case UnitTypes.PAN_FEEDER_SP: {
      if ('otherModel' in unit.unitInfo && unit.unitInfo.otherModel) {
        return unit.unitInfo.otherModelName
          ? `Pan feeder ${unit.unitInfo.otherModelName}`
          : 'Pan feeder';
      }

      const description = modelName
        ? getDescriptionFromModelName(
            unitInfoData[UnitTypes.PAN_FEEDER_SP],
            modelName,
          )
        : '';

      return description ? `Pan feeder ${description}` : 'Pan feeder';
    }
    case UnitTypes.PLATE_FEEDER_SH:
      return modelName ? `Plate feeder ${modelName}` : 'Plate feeder';
    case UnitTypes.GRIZZLY_FEEDER_SV:
    case UnitTypes.GRIZZLY_FEEDER_SV_E:
    case UnitTypes.GRIZZLY_FEEDER_SW:
      return modelName ? `Grizzly feeder ${modelName}` : 'Grizzly feeder';
    case UnitTypes.MOBILE_TRACKED_CONE:
    case UnitTypes.MOBILE_WHEELED_CONE:
      return getMobileTitle(
        'Mobile cone',
        modelName,
        unitInfoData[UnitTypes.MOBILE_CONE_COMMON][modelName]?.configurations
          .displayName,
      );
    case UnitTypes.MOBILE_TRACKED_JAW:
    case UnitTypes.MOBILE_WHEELED_JAW:
      return getMobileTitle(
        'Mobile jaw',
        modelName,
        unitInfoData[UnitTypes.MOBILE_JAW_COMMON][modelName]?.configurations
          .displayName,
      );
    case UnitTypes.MOBILE_TRACKED_SCREEN:
    case UnitTypes.MOBILE_WHEELED_SCREEN: {
      const displayName = unitInfoData[UnitTypes.MOBILE_SCREEN_COMMON][
        modelName
      ]?.configurations.displayName
        ?.replace('(Discontinued)', '')
        ?.trim();

      return getMobileTitle('Mobile screen', modelName, displayName);
    }
    case UnitTypes.MOBILE_TRACKED_HSI:
    case UnitTypes.MOBILE_WHEELED_HSI:
      return getMobileTitle(
        'Mobile HSI',
        modelName,
        unitInfoData[UnitTypes.MOBILE_HSI_COMMON][modelName]?.configurations
          .displayName,
      );
    case UnitTypes.MOBILE_VSI:
      return getMobileTitle(
        'Mobile VSI',
        modelName,
        unitInfoData[UnitTypes.MOBILE_VSI][modelName]?.configurations
          .displayName,
      );
    case UnitTypes.MOBILE_JAW_AND_CONE:
      return modelName
        ? `Mobile jaw and cone ${modelName}`
        : 'Mobile jaw and cone';
    case UnitTypes.FREE_TEXT_BOX:
      return 'Text box';
    case UnitTypes.BREAKER:
      return 'Breaker';
    default:
      return 'Flow Properties';
  }
};
